import { html, property, state } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../../shared/base';
import { AdvancedFilterRendererProps } from '../shared-types';
import getString from '../strings';

@register('kat-data-grid-filter')
export class DgSearch extends KatLitElement {
  @state()
  showSearchDropDown = false;

  @state()
  clientSideData = true;

  @property()
  searchOptions: { name: string; value: string }[];

  @property()
  searchTerm = '';

  @state()
  selectedDropDownOption?: string = undefined;

  @property()
  isFilterApplied?: boolean = false;

  @state()
  totalRowCount = 0;

  @state()
  totalRowCountAfterFilter = 0;

  @state()
  enableSearch = false;

  @state()
  additionalFilter = false;

  @property({ attribute: false })
  advancedFilterRenderer?: AdvancedFilterRendererProps;

  @property({ attribute: false })
  appendAdvancedFilterComponents: (parent: Element) => void;

  @state()
  katDataGrid: any;

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  private _handleFormSubmit() {
    this.dispatchEvent(
      new CustomEvent('search-term-changed', {
        detail: {
          searchTerm: this.searchTerm,
          dropDownOption:
            this.selectedDropDownOption === 'all'
              ? undefined
              : this.camelize(this.selectedDropDownOption),
        },
      })
    );
  }

  private _handleClearAllFilterSearch() {
    this.searchTerm = '';
    this.dispatchEvent(new CustomEvent('clear-all-filter-search'));
  }

  // Needed for client side getQuickFilterText so making camelize for server as well
  private camelize(str) {
    if (!str) return str;
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
      if (+match === 0) return '';
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
  }

  updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    if (_changedProperties.has('advancedFilterRenderer')) {
      if (this.advancedFilterRenderer) {
        const additionalFilterContainer = this.querySelector(
          '.additional-filter-container'
        );
        this.appendAdvancedFilterComponents(additionalFilterContainer);
      }
    }
  }

  clearFilterEl() {
    if (!this.isFilterApplied) {
      return nothing;
    }

    return html`<div class="search-info-clear">
      ${this.totalRowCountAfterFilter && this.totalRowCount
        ? html`<span class="search-info">
            ${this.totalRowCountAfterFilter} ${getString('dg-results')}
            (${this.totalRowCount - this.totalRowCountAfterFilter}
            ${getString('dg-hidden')})
          </span>`
        : null}
      <span class="clear-all-filters">
        <kat-link
          href="#"
          label="${getString('dg-clear-filters')}"
          variant="link"
          @click="${e => {
            e.preventDefault();
            this._handleClearAllFilterSearch();
          }}"
          part="clear-filters"
        ></kat-link>
      </span>
    </div>`;
  }

  render() {
    return html`
      <div>
        <div
          class=${classMap({
            'filter-wrapper': true,
            'with-search-dropdown': this.showSearchDropDown,
            'with-search': this.enableSearch,
          })}
        >
          ${this.enableSearch || this.isFilterApplied
            ? html`<div class="left-container">
                ${this.enableSearch
                  ? html`<div class="search-form">
                      ${this.showSearchDropDown && this.searchOptions
                        ? html`<kat-dropdown
                              class="search-dropdown"
                              value="all"
                              options="${JSON.stringify(this.searchOptions)}"
                              @change="${e => {
                                this.selectedDropDownOption = e.detail.value;
                              }}"
                              part="search-dropdown"
                            ></kat-dropdown>
                            <kat-input
                              class="search-input"
                              type="search"
                              placeholder="${getString('dg-search')}"
                              kat-aria-label="${getString('dg-search')}"
                              @change="${e => {
                                this.searchTerm = e.target.value;
                                this._handleFormSubmit();
                              }}"
                              @input="${e => {
                                if (!e.target.value?.length)
                                  this.searchTerm = '';
                              }}"
                              @search="${e => {
                                //had to make change on kat-input for 'X' button to work
                                if (e.target.value === '') this.searchTerm = '';
                                this._handleFormSubmit();
                              }}"
                              value=${this.searchTerm}
                              part="search-input"
                            ></kat-input>`
                        : html`<kat-input
                            class="search-input"
                            type="search"
                            placeholder="${getString('dg-search')}"
                            @change="${e => {
                              this.searchTerm = e.target.value;
                              this._handleFormSubmit();
                            }}"
                            @input="${e => {
                              if (!e.target.value?.length) this.searchTerm = '';
                            }}"
                            @search="${e => {
                              //had to make change on kat-input for 'X' button to work
                              if (e.target.value === '') this.searchTerm = '';
                              this._handleFormSubmit();
                            }}"
                            value=${this.searchTerm}
                            part="search-input"
                          ></kat-input>`}
                      <kat-button
                        class="search-button"
                        label="${getString('dg-search')}"
                        size="base"
                        variant="secondary"
                        @click="${this._handleFormSubmit}"
                        part="search-submit"
                      ></kat-button>
                    </div>`
                  : this.clearFilterEl()}
              </div>`
            : nothing}
          ${this.additionalFilter
            ? html`<div class="additional-filter-container">
                <slot name="filter"></slot>
              </div>`
            : nothing}
        </div>
        ${this.enableSearch ? this.clearFilterEl() : nothing}
      </div>
    `;
  }
}
